import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Mail, Lock, User, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../App';
import { toast } from 'sonner';

const AuthPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if coming from campaign page with pre-filled email
  const prefilledEmail = location.state?.email || '';
  const prefilledMode = location.state?.mode;
  
  const [isLogin, setIsLogin] = useState(prefilledMode !== 'register');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: prefilledEmail,
    password: '',
    name: ''
  });

  const { login, register } = useAuth();
  const from = location.state?.from?.pathname || '/discover';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Velkommen tilbage!');
      } else {
        await register(formData.email, formData.password, formData.name);
        toast.success('Konto oprettet! Lad os sætte din profil op.');
        navigate('/setup');
        return;
      }
      navigate(from);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Noget gik galt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md w-full mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Tilbage til forsiden
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-center gap-2 mb-8">
              <Heart className="w-8 h-8 text-primary fill-primary" />
              <span className="text-2xl font-display font-semibold text-foreground">Flirra</span>
            </div>

            <h1 className="text-3xl font-display font-bold text-foreground mb-2">
              {isLogin ? 'Velkommen tilbage' : 'Opret din konto'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {isLogin
                ? 'Indtast dine oplysninger for at fortsætte'
                : 'Start din rejse mod at finde kærligheden'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Navn</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Dit navn"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 h-12 rounded-xl"
                      required={!isLogin}
                      data-testid="auth-name-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="dig@eksempel.dk"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 h-12 rounded-xl"
                    required
                    data-testid="auth-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Adgangskode</Label>
                  {isLogin && (
                    <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                      Glemt adgangskode?
                    </Link>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 h-12 rounded-xl"
                    required
                    minLength={6}
                    data-testid="auth-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-base"
                disabled={loading}
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : isLogin ? (
                  'Log ind'
                ) : (
                  'Opret konto'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-primary hover:underline text-sm font-medium"
                data-testid="auth-toggle-btn"
              >
                {isLogin
                  ? "Har du ikke en konto? Opret dig"
                  : 'Har du allerede en konto? Log ind'}
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/3978157/pexels-photo-3978157.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800"
          alt="Par på café"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <blockquote className="text-2xl font-display italic">
            "Jeg troede aldrig på online dating, indtil jeg fandt min sjælefrænde på Flirra."
          </blockquote>
          <p className="mt-4 text-white/80">— Sarah & Mike, København</p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
